import React, { useState, useEffect } from "react";
import {
  Plus,
  Trash2,
  Calculator,
  Edit3,
  X,
  Save,
  ArrowLeft,
  FileText,
  Eye,
  Link2,
  CheckCircle,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useParams } from "react-router-dom";
import { getAllCourseAssignments } from "../../../../services/assignment.service";
import {
  getContinuousAssessmentPlan,
  createContinuousAssessmentCategory,
  updateContinuousAssessmentCategory,
  deleteContinuousAssessmentCategory,
  getAssessmentPlan,
} from "../../../../services/course.service";
import { useUtilityContext } from "../../../../context/UtilityContext";
import CreateAssignmentNew from "../../../Assignment/teacher/CreateAssignmentNew";
import toast from "react-hot-toast";
import {
  card,
  pageTitle,
  bodySecondary,
  accentBarSection,
  cardTitle,
  microLabel,
  dividerInner,
  iconSize,
  emptyState,
  motionFadeUp,
  motionStaggerChild,
} from "../../../../lib/design-system";
import { cn } from "../../../../lib/utils";
import { Skeleton, SkeletonCard } from "../../../StudentDashboard/Components/ui/Skeleton";

const ContinuousAssessment = () => {
  const { courseID: urlCourseID } = useParams();
  const { selectedCourseId } = useUtilityContext();
  const courseID = urlCourseID || selectedCourseId;

  const [assessmentCategories, setAssessmentCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalMarks, setTotalMarks] = useState(0);
  const [assessmentPlan, setAssessmentPlan] = useState({
    endTermExam: 50,
    midTermExam: 30,
    continuousAssessment: 20,
  });

  // Per-card editing
  const [editingCategoryId, setEditingCategoryId] = useState(null);
  const [editBuffer, setEditBuffer] = useState(null);

  // Assignment modal
  const [showAssignmentModal, setShowAssignmentModal] = useState(false);
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  const [availableAssignments, setAvailableAssignments] = useState([]);
  const [selectedAssignments, setSelectedAssignments] = useState({});
  const [loadingAssignments, setLoadingAssignments] = useState(false);

  // Create assignment
  const [isCreatingAssignment, setIsCreatingAssignment] = useState(false);
  const [createAssignmentType, setCreateAssignmentType] = useState("subjective");

  const categoryOptions = [
    "General Assignment",
    "Objective one",
    "Activity-Based Assignment",
    "Quiz Test",
    "Engagement in Lectures",
    "Presentation/viva",
    "Lab Report",
    "Project Report",
    "Group Project",
    "Individual Project",
    "Case Study",
    "Research Paper",
  ];

  const calculationOptions = [
    "Average of Best of n",
    "Average of all",
    "Average of the Best 'n'",
    "Sum of all",
    "Sum of the Best 'n'",
  ];

  // ── Helpers ──────────────────────────────────────────────────────────

  const startEditing = (cat) => {
    setEditingCategoryId(cat._id || cat.id);
    setEditBuffer({
      category: cat.category,
      number: cat.number,
      eachMarks: cat.eachMarks,
      calculationMethod: cat.calculationMethod,
      n: cat.n,
    });
  };

  const cancelEditing = () => {
    setEditingCategoryId(null);
    setEditBuffer(null);
  };

  const saveEditing = async (id) => {
    if (!editBuffer) return;
    const category = assessmentCategories.find((c) => (c._id || c.id) === id);
    if (!category) return;

    // Recalculate total marks
    const num = parseInt(editBuffer.number) || 0;
    const eachMarks = parseInt(editBuffer.eachMarks) || 0;
    const n = parseInt(editBuffer.n) || 0;
    let newTotal = 0;
    switch (editBuffer.calculationMethod) {
      case "Average of all":
        newTotal = eachMarks;
        break;
      case "Sum of all":
        newTotal = num * eachMarks;
        break;
      case "Average of Best of n":
      case "Average of the Best 'n'":
      case "Sum of the Best 'n'":
        newTotal = Math.min(n, num) * eachMarks;
        break;
      default:
        newTotal = eachMarks;
    }

    const updated = { ...category, ...editBuffer, totalMarks: newTotal };

    // Optimistic update
    setAssessmentCategories((prev) =>
      prev.map((c) => ((c._id || c.id) === id ? updated : c))
    );
    setEditingCategoryId(null);
    setEditBuffer(null);

    try {
      const categoryId = category._id || category.id;
      await updateContinuousAssessmentCategory(courseID, categoryId, {
        category: editBuffer.category,
        number: editBuffer.number,
        eachMarks: editBuffer.eachMarks,
        calculationMethod: editBuffer.calculationMethod,
        n: editBuffer.n,
        totalMarks: newTotal,
      });
    } catch (error) {
      console.error("Error updating category:", error);
      toast.error(error.response?.data?.error || "Failed to update category");
      // Revert
      setAssessmentCategories((prev) =>
        prev.map((c) => ((c._id || c.id) === id ? category : c))
      );
    }
  };

  const closeModal = () => {
    setShowAssignmentModal(false);
    setSelectedAssignments({});
    setSelectedCategoryId(null);
  };

  // ── Data fetching ───────────────────────────────────────────────────

  useEffect(() => {
    const fetchData = async () => {
      if (!courseID) {
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        const planData = await getAssessmentPlan(courseID);
        if (planData) {
          setAssessmentPlan({
            endTermExam: planData.endTermExam,
            midTermExam: planData.midTermExam,
            continuousAssessment: planData.continuousAssessment,
          });
        }
        const categories = await getContinuousAssessmentPlan(courseID);
        if (categories && categories.length > 0) {
          const formatted = categories.map((cat) => ({
            id: cat._id,
            _id: cat._id,
            category: cat.category,
            number: cat.number,
            eachMarks: cat.eachMarks,
            calculationMethod: cat.calculationMethod,
            n: cat.n,
            totalMarks: cat.totalMarks,
            selectedAssignments: cat.selectedAssignments || [],
            assignmentDetails: cat.selectedAssignments || [],
          }));
          setAssessmentCategories(formatted);
        } else {
          setAssessmentCategories([]);
        }
      } catch (error) {
        console.error("Error fetching continuous assessment data:", error);
        toast.error("Failed to load continuous assessment plan");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [courseID]);

  useEffect(() => {
    const total = assessmentCategories.reduce(
      (sum, cat) => sum + (cat.totalMarks || 0),
      0
    );
    setTotalMarks(total);
  }, [assessmentCategories]);

  // ── Actions ─────────────────────────────────────────────────────────

  const addNewCategory = async () => {
    try {
      const newCategoryData = {
        category: "General Assignment",
        number: 1,
        eachMarks: 10,
        calculationMethod: "Average of Best of n",
        n: 1,
        totalMarks: 10,
        selectedAssignments: [],
      };
      const created = await createContinuousAssessmentCategory(courseID, newCategoryData);
      const newCategory = {
        id: created._id || created.data?._id,
        _id: created._id || created.data?._id,
        category: created.category || created.data?.category,
        number: created.number || created.data?.number,
        eachMarks: created.eachMarks || created.data?.eachMarks,
        calculationMethod: created.calculationMethod || created.data?.calculationMethod,
        n: created.n || created.data?.n,
        totalMarks: created.totalMarks || created.data?.totalMarks,
        selectedAssignments: created.selectedAssignments || created.data?.selectedAssignments || [],
      };
      setAssessmentCategories((prev) => [...prev, newCategory]);
      // Auto-open edit mode on the new card
      startEditing(newCategory);
      toast.success("Category added successfully");
    } catch (error) {
      console.error("Error creating category:", error);
      toast.error(error.response?.data?.error || "Failed to create category");
    }
  };

  const deleteCategory = async (id) => {
    const category = assessmentCategories.find((c) => (c.id === id || c._id === id));
    if (!category) return;
    try {
      const categoryId = category._id || category.id;
      await deleteContinuousAssessmentCategory(courseID, categoryId);
      setAssessmentCategories((prev) => prev.filter((c) => c.id !== id && c._id !== id));
      if (editingCategoryId === id) cancelEditing();
      toast.success("Category deleted successfully");
    } catch (error) {
      console.error("Error deleting category:", error);
      toast.error(error.response?.data?.error || "Failed to delete category");
    }
  };

  const fetchAssignments = async (categoryType) => {
    if (!courseID) return;
    try {
      setLoadingAssignments(true);
      const response = await getAllCourseAssignments({ courseID });
      const assignments = response.assignments || [];
      let filtered = [];
      if (categoryType === "General Assignment") {
        filtered = assignments.filter((a) =>
          (a.questions || []).some((q) => q.type === "subjective")
        );
      } else if (categoryType === "Objective one") {
        filtered = assignments.filter((a) =>
          (a.questions || []).some((q) => q.type === "objective")
        );
      }
      setAvailableAssignments(filtered);
    } catch (error) {
      console.error("Error fetching assignments:", error);
    } finally {
      setLoadingAssignments(false);
    }
  };

  const handleAddNew = (categoryType) => {
    setCreateAssignmentType(categoryType === "General Assignment" ? "subjective" : "objective");
    setIsCreatingAssignment(true);
  };

  const handleBackFromCreate = () => {
    setIsCreatingAssignment(false);
    setCreateAssignmentType("subjective");
  };

  const handleAssignmentSaved = () => {
    setIsCreatingAssignment(false);
    setCreateAssignmentType("subjective");
    toast.success("Assignment created successfully!");
  };

  const handleSelect = async (id, categoryType) => {
    setSelectedCategoryId(id);
    await fetchAssignments(categoryType);
    setShowAssignmentModal(true);
    const category = assessmentCategories.find((c) => (c.id === id || c._id === id));
    if (category?.selectedAssignments) {
      const selected = {};
      category.selectedAssignments.forEach((aId) => {
        const key = typeof aId === "string" ? aId : aId._id || aId.id;
        selected[key] = true;
      });
      setSelectedAssignments(selected);
    } else {
      setSelectedAssignments({});
    }
  };

  const handleAssignmentToggle = (assignmentId) => {
    setSelectedAssignments((prev) => ({ ...prev, [assignmentId]: !prev[assignmentId] }));
  };

  const handleSaveSelectedAssignments = async () => {
    const selectedIds = Object.keys(selectedAssignments).filter((id) => selectedAssignments[id]);
    const category = assessmentCategories.find(
      (c) => (c.id === selectedCategoryId || c._id === selectedCategoryId)
    );
    if (!category) return;
    try {
      const categoryId = category._id || category.id;
      await updateContinuousAssessmentCategory(courseID, categoryId, {
        selectedAssignments: selectedIds,
      });
      setAssessmentCategories((prev) =>
        prev.map((c) =>
          (c.id === selectedCategoryId || c._id === selectedCategoryId)
            ? { ...c, selectedAssignments: selectedIds }
            : c
        )
      );
      closeModal();
      toast.success("Assignments linked successfully");
    } catch (error) {
      console.error("Error saving selected assignments:", error);
      toast.error(error.response?.data?.error || "Failed to save assignments");
    }
  };

  const remainingMarks = assessmentPlan.continuousAssessment - totalMarks;
  const progressPct = assessmentPlan.continuousAssessment > 0
    ? Math.min((totalMarks / assessmentPlan.continuousAssessment) * 100, 100)
    : 0;
  const selectedCount = Object.keys(selectedAssignments).filter((id) => selectedAssignments[id]).length;

  // ── Loading skeleton ────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="space-y-6 p-4 sm:p-6">
        {/* Header skeleton */}
        <div>
          <Skeleton className="h-7 w-64" />
          <Skeleton className="h-1 w-12 mt-2" />
          <Skeleton className="h-4 w-80 mt-3" />
        </div>
        {/* Progress bar skeleton */}
        <SkeletonCard className="p-5">
          <div className="flex items-center justify-between mb-3">
            <Skeleton className="h-3 w-28" />
            <Skeleton className="h-4 w-16" />
          </div>
          <Skeleton className="h-5 w-full" />
          <Skeleton className="h-3 w-48 mt-3" />
        </SkeletonCard>
        {/* Category card skeletons */}
        {[...Array(3)].map((_, i) => (
          <SkeletonCard key={i} className="overflow-hidden">
            <div className={cn("px-5 py-4 border-b", dividerInner, "flex items-center justify-between")}>
              <Skeleton className="h-5 w-40" />
              <div className="flex gap-2">
                <Skeleton className="h-7 w-14" />
                <Skeleton className="h-7 w-7" />
              </div>
            </div>
            <div className="p-5 grid grid-cols-2 sm:grid-cols-4 gap-4">
              {[...Array(4)].map((_, j) => (
                <div key={j} className="space-y-1.5">
                  <Skeleton className="h-2.5 w-16" />
                  <Skeleton className="h-5 w-12" />
                </div>
              ))}
            </div>
          </SkeletonCard>
        ))}
      </div>
    );
  }

  // ── Create assignment view ──────────────────────────────────────────

  if (isCreatingAssignment) {
    return (
      <motion.div className="space-y-6 p-4 sm:p-6" {...motionFadeUp}>
        <div className={cn(card, "overflow-hidden")}>
          <div className={cn("px-5 py-4 border-b", dividerInner)}>
            <button
              onClick={handleBackFromCreate}
              className="flex items-center gap-2 text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white mb-3 transition-colors text-sm font-medium"
            >
              <ArrowLeft className={iconSize.md} />
              Back to Continuous Assessment
            </button>
            <h2 className={pageTitle}>
              Create New {createAssignmentType === "subjective" ? "General" : "Objective"} Assignment
            </h2>
            <div className={cn(accentBarSection, "mt-2")} />
            <p className={cn(bodySecondary, "mt-2")}>
              Create a new assignment to include in your continuous assessment plan
            </p>
          </div>
          <div className="p-5">
            <CreateAssignmentNew
              courseID={courseID}
              inModal={false}
              assignmentType={createAssignmentType}
              onSave={handleAssignmentSaved}
              onCancel={handleBackFromCreate}
            />
          </div>
        </div>
      </motion.div>
    );
  }

  // ── Main render ─────────────────────────────────────────────────────

  return (
    <motion.div className="space-y-6 p-4 sm:p-6" {...motionFadeUp}>
      {/* Page header */}
      <div className="flex items-start justify-between">
        <div>
          <h2 className={pageTitle}>Continuous Assessment Plan</h2>
          <div className={cn(accentBarSection, "mt-2")} />
          <p className={cn(bodySecondary, "mt-2")}>
            Plan and configure assessment categories for your course
          </p>
        </div>
        <button
          onClick={addNewCategory}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm bg-accent1 text-white text-xs font-bold uppercase tracking-wider hover:bg-accent1/90 transition-colors"
        >
          <Plus className={iconSize.sm} />
          Add Category
        </button>
      </div>

      {/* Summary progress bar */}
      <div className={cn(card, "p-5")}>
        <div className="flex items-center justify-between mb-2">
          <span className={microLabel}>Marks Allocation</span>
          <span className="text-sm font-black tracking-tight text-gray-900 dark:text-white">
            {totalMarks}/{assessmentPlan.continuousAssessment}
          </span>
        </div>
        <div className="h-5 rounded-sm overflow-hidden bg-gray-100 dark:bg-gray-700">
          <motion.div
            className={cn(
              "h-full rounded-sm",
              remainingMarks < 0 ? "bg-red-500" : "bg-accent1"
            )}
            initial={{ width: 0 }}
            animate={{ width: `${progressPct}%` }}
            transition={{ duration: 0.5 }}
          />
        </div>
        {remainingMarks > 0 && (
          <p className="text-xs font-medium text-amber-600 dark:text-amber-400 mt-2">
            {remainingMarks} marks remaining to allocate
          </p>
        )}
        {remainingMarks < 0 && (
          <p className="text-xs font-medium text-red-600 dark:text-red-400 mt-2">
            Exceeded by {Math.abs(remainingMarks)} marks — please adjust
          </p>
        )}
        {remainingMarks === 0 && totalMarks > 0 && (
          <p className="text-xs font-medium text-emerald-600 dark:text-emerald-400 mt-2">
            All {assessmentPlan.continuousAssessment} marks allocated perfectly
          </p>
        )}
      </div>

      {/* Empty state */}
      {assessmentCategories.length === 0 && (
        <div className={emptyState}>
          <Calculator className={cn(iconSize.empty, "mx-auto text-gray-400 dark:text-gray-500 mb-3")} />
          <p className="text-sm font-bold text-gray-600 dark:text-gray-300 mb-1">
            No categories yet
          </p>
          <p className={bodySecondary}>
            Add your first category to start building your assessment plan
          </p>
          <button
            onClick={addNewCategory}
            className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold uppercase tracking-wide text-accent1 hover:text-accent1/80 transition-colors"
          >
            <Plus className={iconSize.sm} />
            Add Category
          </button>
        </div>
      )}

      {/* Category cards */}
      <AnimatePresence mode="popLayout">
        {assessmentCategories.map((category, index) => {
          const catId = category._id || category.id;
          const isEditing = editingCategoryId === catId;
          const buf = isEditing ? editBuffer : null;
          const linkedCount = category.selectedAssignments?.length || 0;
          const showAssignmentActions =
            category.category === "General Assignment" ||
            category.category === "Objective one";

          return (
            <motion.div
              key={catId}
              layout
              className={cn(card, "overflow-hidden")}
              {...motionStaggerChild(index)}
              exit={{ opacity: 0, y: -8, transition: { duration: 0.2 } }}
            >
              {/* Card header */}
              <div
                className={cn(
                  "px-5 py-3.5 border-b flex items-center justify-between",
                  dividerInner
                )}
              >
                <div className="flex items-center gap-2.5">
                  <h3 className={cardTitle}>{category.category}</h3>
                  {linkedCount > 0 && (
                    <span className="px-2 py-0.5 rounded-sm text-[10px] font-bold uppercase bg-accent1/10 text-accent1">
                      {linkedCount} linked
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {!isEditing ? (
                    <button
                      onClick={() => startEditing(category)}
                      className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-sm bg-accent1/10 text-accent1 text-xs font-bold uppercase tracking-wider hover:bg-accent1/20 transition-colors"
                    >
                      <Edit3 className={iconSize.xs} />
                      Edit
                    </button>
                  ) : (
                    <>
                      <button
                        onClick={cancelEditing}
                        className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-sm bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 text-xs font-bold uppercase tracking-wider hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                      >
                        <X className={iconSize.xs} />
                        Cancel
                      </button>
                      <button
                        onClick={() => saveEditing(catId)}
                        className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-sm bg-accent1 text-white text-xs font-bold uppercase tracking-wider hover:bg-accent1/90 transition-colors"
                      >
                        <Save className={iconSize.xs} />
                        Save
                      </button>
                    </>
                  )}
                  <button
                    onClick={() => deleteCategory(catId)}
                    className="p-1.5 rounded-sm text-gray-400 dark:text-gray-500 hover:text-red-500 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                    title="Delete category"
                  >
                    <Trash2 className={iconSize.md} />
                  </button>
                </div>
              </div>

              {/* Card body */}
              <div className="p-5">
                {isEditing ? (
                  /* ── Edit mode ── */
                  <div className="space-y-4">
                    {/* Category selector — full width */}
                    <div>
                      <label className={cn(microLabel, "mb-1.5 block")}>Category</label>
                      <select
                        value={buf.category}
                        onChange={(e) =>
                          setEditBuffer((prev) => ({ ...prev, category: e.target.value }))
                        }
                        className="w-full p-2 rounded-sm border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-accent1/30 focus:border-accent1 outline-none transition-colors"
                      >
                        {categoryOptions.map((opt) => (
                          <option key={opt} value={opt}>
                            {opt}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Fields grid */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                      <div>
                        <label className={cn(microLabel, "mb-1.5 block")}>Number</label>
                        <input
                          type="number"
                          value={buf.number}
                          onChange={(e) =>
                            setEditBuffer((prev) => ({ ...prev, number: e.target.value }))
                          }
                          className="w-full p-2 rounded-sm border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-accent1/30 focus:border-accent1 outline-none transition-colors"
                          min="1"
                        />
                      </div>
                      <div>
                        <label className={cn(microLabel, "mb-1.5 block")}>Each Marks</label>
                        <input
                          type="number"
                          value={buf.eachMarks}
                          onChange={(e) =>
                            setEditBuffer((prev) => ({ ...prev, eachMarks: e.target.value }))
                          }
                          className="w-full p-2 rounded-sm border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-accent1/30 focus:border-accent1 outline-none transition-colors"
                          min="1"
                        />
                      </div>
                      <div>
                        <label className={cn(microLabel, "mb-1.5 block")}>Calculation</label>
                        <select
                          value={buf.calculationMethod}
                          onChange={(e) =>
                            setEditBuffer((prev) => ({
                              ...prev,
                              calculationMethod: e.target.value,
                            }))
                          }
                          className="w-full p-2 rounded-sm border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-accent1/30 focus:border-accent1 outline-none transition-colors"
                        >
                          {calculationOptions.map((opt) => (
                            <option key={opt} value={opt}>
                              {opt}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className={cn(microLabel, "mb-1.5 block")}>n</label>
                        <input
                          type="number"
                          value={buf.n}
                          onChange={(e) =>
                            setEditBuffer((prev) => ({ ...prev, n: e.target.value }))
                          }
                          className="w-full p-2 rounded-sm border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-accent1/30 focus:border-accent1 outline-none transition-colors"
                          min="1"
                        />
                      </div>
                    </div>
                  </div>
                ) : (
                  /* ── View mode ── */
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                    <div>
                      <span className={cn(microLabel, "block mb-1")}>Number</span>
                      <span className="text-sm font-bold text-gray-900 dark:text-white">
                        {category.number}
                      </span>
                    </div>
                    <div>
                      <span className={cn(microLabel, "block mb-1")}>Each Marks</span>
                      <span className="text-sm font-bold text-gray-900 dark:text-white">
                        {category.eachMarks}
                      </span>
                    </div>
                    <div>
                      <span className={cn(microLabel, "block mb-1")}>Calculation</span>
                      <span className="text-sm font-bold text-gray-900 dark:text-white">
                        {category.calculationMethod}
                      </span>
                    </div>
                    <div>
                      <span className={cn(microLabel, "block mb-1")}>Total Marks</span>
                      <span className="text-sm font-black text-accent1">
                        {category.totalMarks}
                      </span>
                    </div>
                  </div>
                )}

                {/* Assignment actions */}
                {showAssignmentActions && (
                  <div className={cn("mt-4 pt-4 border-t flex flex-wrap gap-2", dividerInner)}>
                    <button
                      onClick={() => handleSelect(catId, category.category)}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm border border-accent1/30 text-accent1 text-xs font-bold uppercase tracking-wider hover:bg-accent1/10 transition-colors"
                    >
                      <Link2 className={iconSize.xs} />
                      Link Assignments
                    </button>
                    <button
                      onClick={() => handleAddNew(category.category)}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 text-xs font-bold uppercase tracking-wider hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                    >
                      <Plus className={iconSize.xs} />
                      Create New
                    </button>
                    <button
                      onClick={() => {
                        const sectionName =
                          category.category === "General Assignment" ? "Subjective" : "Objective";
                        localStorage.setItem(`course_${courseID}_selectedSection`, sectionName);
                        window.dispatchEvent(
                          new CustomEvent("sectionChange", { detail: { section: sectionName } })
                        );
                      }}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-gray-500 dark:text-gray-400 text-xs font-bold uppercase tracking-wider hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
                    >
                      <Eye className={iconSize.xs} />
                      View All
                    </button>
                  </div>
                )}
              </div>
            </motion.div>
          );
        })}
      </AnimatePresence>

      {/* ── Assignment selection modal ── */}
      {showAssignmentModal && (
        <div className="fixed inset-0 bg-black/50 dark:bg-black/70 flex items-center justify-center z-50 p-4">
          <motion.div
            className={cn(card, "max-w-2xl w-full max-h-[80vh] flex flex-col overflow-hidden")}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2 }}
          >
            {/* Modal header */}
            <div className={cn("px-5 py-4 border-b flex items-center justify-between", dividerInner)}>
              <div>
                <h3 className={cardTitle}>Select Assignments</h3>
                <p className={cn(bodySecondary, "mt-0.5")}>
                  Choose assignments to include in this category
                </p>
              </div>
              <button
                onClick={closeModal}
                className="p-1.5 rounded-sm text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                <X className={iconSize.lg} />
              </button>
            </div>

            {/* Modal body */}
            <div className="flex-1 overflow-y-auto p-5">
              {loadingAssignments ? (
                <div className="space-y-3">
                  {[...Array(4)].map((_, i) => (
                    <div key={i} className="flex items-start gap-3 p-4 rounded-sm border border-gray-200 dark:border-gray-700">
                      <Skeleton className="h-5 w-5 mt-0.5 flex-shrink-0" />
                      <div className="flex-1 space-y-2">
                        <Skeleton className="h-4 w-3/4" />
                        <Skeleton className="h-3 w-full" />
                        <Skeleton className="h-3 w-24" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : availableAssignments.length === 0 ? (
                <div className={emptyState}>
                  <FileText className={cn(iconSize.empty, "mx-auto text-gray-400 dark:text-gray-500 mb-3")} />
                  <p className="text-sm font-bold text-gray-600 dark:text-gray-300 mb-1">
                    No assignments available
                  </p>
                  <button
                    onClick={() => {
                      closeModal();
                      handleAddNew(
                        assessmentCategories.find(
                          (c) => (c.id === selectedCategoryId || c._id === selectedCategoryId)
                        )?.category || "General Assignment"
                      );
                    }}
                    className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold uppercase tracking-wide text-accent1 hover:text-accent1/80 transition-colors"
                  >
                    <Plus className={iconSize.sm} />
                    Create New Assignment
                  </button>
                </div>
              ) : (
                <div className="space-y-2">
                  {availableAssignments.map((assignment) => (
                    <label
                      key={assignment._id}
                      className={cn(
                        "flex items-start gap-3 p-4 rounded-sm border-2 cursor-pointer transition-all",
                        selectedAssignments[assignment._id]
                          ? "border-accent1 bg-accent1/5 dark:bg-accent1/10"
                          : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-800"
                      )}
                    >
                      <div className="mt-0.5">
                        {selectedAssignments[assignment._id] ? (
                          <CheckCircle className="h-5 w-5 text-accent1" />
                        ) : (
                          <div className="h-5 w-5 rounded-sm border-2 border-gray-300 dark:border-gray-600" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-bold text-gray-900 dark:text-white">
                          {assignment.title}
                        </div>
                        {assignment.description && (
                          <div className={cn(bodySecondary, "mt-1 line-clamp-2")}>
                            {assignment.description}
                          </div>
                        )}
                        <div className="flex items-center gap-4 mt-2">
                          <span className={microLabel}>{assignment.totalPoints} pts</span>
                          {assignment.dueDate && (
                            <span className={microLabel}>
                              Due: {new Date(assignment.dueDate).toLocaleDateString()}
                            </span>
                          )}
                        </div>
                      </div>
                      <input
                        type="checkbox"
                        checked={selectedAssignments[assignment._id] || false}
                        onChange={() => handleAssignmentToggle(assignment._id)}
                        className="sr-only"
                      />
                    </label>
                  ))}
                </div>
              )}
            </div>

            {/* Modal footer */}
            <div className={cn("px-5 py-4 border-t flex items-center justify-between", dividerInner)}>
              <span className={bodySecondary}>
                {selectedCount} assignment{selectedCount !== 1 ? "s" : ""} selected
              </span>
              <div className="flex gap-2">
                <button
                  onClick={closeModal}
                  className="px-3 py-1.5 rounded-sm bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 text-xs font-bold uppercase tracking-wider hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveSelectedAssignments}
                  disabled={selectedCount === 0}
                  className="px-3 py-1.5 rounded-sm bg-accent1 text-white text-xs font-bold uppercase tracking-wider hover:bg-accent1/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Link ({selectedCount})
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </motion.div>
  );
};

export default ContinuousAssessment;
